import { ExternalLink, Loader2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DocumentFlagIcons } from '@/components/documents/DocumentFlagIcons';
import { DocumentStatusBadge } from '@/components/documents/DocumentStatusBadge';
import { VerificationControls } from '@/components/documents/VerificationControls';
import { cn } from '@/lib/utils';
import {
  getDocument,
  getDocumentPreviewUrl,
  rejectDocument,
  requestReupload,
  verifyDocument,
} from '@/services/documentService';

import type { DocumentStatus, HrDocument, PreviewUrlData } from '@/types/document.types';
import type { SubmissionDetail } from '@/types/submission.types';

interface DocumentPreviewModalProps {
  documentId: string;
  isOpen: boolean;
  onClose: () => void;
  onStatusChange: () => void;
  applicationData?: SubmissionDetail;
}

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg']);

function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return (parts[parts.length - 1] ?? '').toLowerCase();
}

function isImageFile(filename: string): boolean {
  return IMAGE_EXTENSIONS.has(getFileExtension(filename));
}

function formatTypeName(type: string): string {
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function asObject(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  return value as Record<string, unknown>;
}

function toStringOrUndefined(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function toNumberOrUndefined(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

export function DocumentPreviewModal({
  documentId,
  isOpen,
  onClose,
  onStatusChange,
  applicationData,
}: DocumentPreviewModalProps) {
  const { t } = useTranslation();

  const [document, setDocument] = useState<HrDocument | null>(null);
  const [preview, setPreview] = useState<PreviewUrlData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [doc, prev] = await Promise.all([getDocument(documentId), getDocumentPreviewUrl(documentId)]);
      setDocument(doc);
      setPreview(prev);
    } catch {
      setError(t('documents.preview.errorLoading'));
    } finally {
      setIsLoading(false);
    }
  }, [documentId, t]);

  useEffect(() => {
    if (isOpen) {
      void loadData();
    }
    return () => {
      setDocument(null);
      setPreview(null);
      setError(null);
    };
  }, [isOpen, loadData]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleVerify = async (id: string) => {
    await verifyDocument(id);
    onStatusChange();
    void loadData();
  };

  const handleReject = async (id: string, reason: string) => {
    await rejectDocument(id, reason);
    onStatusChange();
    void loadData();
  };

  const handleReupload = async (id: string, reason: string) => {
    await requestReupload(id, reason);
    onStatusChange();
    void loadData();
  };

  const handleOpenNewTab = () => {
    if (preview?.previewUrl) {
      window.open(preview.previewUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const extractedView = useMemo(() => {
    if (!document?.extracted) {
      return undefined;
    }

    const extracted = document.extracted;
    return {
      name: extracted.name,
      dob: extracted.dateOfBirth ?? extracted.dob,
      number: extracted.documentNumber ?? extracted.number,
      expiresOn: extracted.expirationDate ?? extracted.expiresOn,
      issuedDate: extracted.issuedDate,
      issuer: extracted.issuingState ?? extracted.issuer,
      address: extracted.address,
      sex: extracted.sex,
      height: extracted.height,
      eyeColor: extracted.eyeColor,
      licenseClass: extracted.licenseClass,
      restrictions: extracted.restrictions,
      endorsements: extracted.endorsements,
      confidence: extracted.confidence,
    };
  }, [document]);

  const extractedHasValues = useMemo(() => {
    if (!extractedView) {
      return false;
    }

    return (
      [
        extractedView.name,
        extractedView.dob,
        extractedView.number,
        extractedView.expiresOn,
        extractedView.issuedDate,
        extractedView.issuer,
        extractedView.address,
        extractedView.sex,
        extractedView.height,
        extractedView.eyeColor,
        extractedView.licenseClass,
      ].some(Boolean) || extractedView.confidence !== undefined
    );
  }, [extractedView]);

  const flagDetails = useMemo(() => {
    if (!document?.flags) {
      return [] as string[];
    }

    const flags = document.flags;
    const details: string[] = [];

    if (flags.nameMismatch) {
      const mismatch = asObject(flags.nameMismatch);
      const expected = toStringOrUndefined(mismatch?.expected);
      const extracted = toStringOrUndefined(mismatch?.extracted);

      if (expected && extracted) {
        details.push(t('documents.preview.flagDetails.nameMismatch', { expected, extracted }));
      } else {
        details.push(t('documents.preview.flagDetails.nameMismatchGeneric'));
      }
    }

    if (flags.expired) {
      const expired = asObject(flags.expired);
      const expirationDate = toStringOrUndefined(expired?.expirationDate);
      if (expirationDate) {
        details.push(t('documents.preview.flagDetails.expired', { expirationDate }));
      } else {
        details.push(t('documents.preview.flagDetails.expiredGeneric'));
      }
    }

    if (flags.expiringSoon) {
      const expiringSoon = asObject(flags.expiringSoon);
      const expirationDate = toStringOrUndefined(expiringSoon?.expirationDate);
      const daysUntilExpiry = toNumberOrUndefined(expiringSoon?.daysUntilExpiry);
      if (expirationDate && daysUntilExpiry !== undefined) {
        details.push(t('documents.preview.flagDetails.expiringSoon', { expirationDate, daysUntilExpiry }));
      } else {
        details.push(t('documents.preview.flagDetails.expiringSoonGeneric'));
      }
    }

    if (flags.illegible) {
      const illegible = asObject(flags.illegible);
      const reason = toStringOrUndefined(illegible?.reason);
      if (reason) {
        details.push(t('documents.preview.flagDetails.illegible', { reason }));
      } else {
        details.push(t('documents.preview.flagDetails.illegibleGeneric'));
      }
    }

    if (flags.reuploadRequested) {
      details.push(t('documents.preview.flagDetails.reuploadRequested'));
    }

    return details;
  }, [document, t]);

  const crossValidationRows = useMemo(() => {
    if (!document?.extracted || !applicationData) return [];

    const rows: Array<{ field: string; appValue: string; docValue: string; isMatch: boolean }> = [];
    const extracted = document.extracted;

    // Name comparison
    const appName = `${applicationData.stepA.firstName} ${applicationData.stepA.lastName}`;
    if (extracted.name) {
      const normApp = appName.toLowerCase().trim();
      const normDoc = extracted.name.toLowerCase().trim();
      rows.push({
        field: t('documents.preview.extracted.name'),
        appValue: appName,
        docValue: extracted.name,
        isMatch: normApp === normDoc,
      });
    }

    // Document number comparison
    const extractedNum = extracted.documentNumber ?? extracted.number;
    if (extractedNum) {
      const appNumbers = [
        applicationData.stepC.florida?.classDNumber,
        applicationData.stepC.florida?.classGNumber,
        applicationData.stepC.newYork?.guardCardNumber,
        applicationData.stepC.newYork?.firearmCredsNumber,
      ].filter((n): n is string => !!n);

      for (const appNum of appNumbers) {
        const normApp = appNum.replace(/[\s\-]/g, '').toLowerCase();
        const normDoc = extractedNum.replace(/[\s\-]/g, '').toLowerCase();
        rows.push({
          field: t('documents.preview.extracted.number'),
          appValue: appNum,
          docValue: extractedNum,
          isMatch: normApp === normDoc,
        });
      }
    }

    // Expiration date comparison
    const extractedExp = extracted.expirationDate ?? extracted.expiresOn;
    if (extractedExp) {
      const parseDate = (d: string): string => {
        const parsed = new Date(d);
        if (isNaN(parsed.getTime())) return d;
        return parsed.toISOString().split('T')[0] ?? d;
      };

      const appDates = [
        applicationData.stepC.florida?.classDExpiration,
        applicationData.stepC.florida?.classGExpiration,
        applicationData.stepC.newYork?.guardCardExpiration,
        applicationData.stepC.newYork?.firearmCredsExpiration,
        applicationData.stepC.certifications.cprAedExpiration,
        applicationData.stepC.certifications.twicExpiration,
      ].filter((d): d is string => !!d);

      for (const appDate of appDates) {
        rows.push({
          field: t('documents.preview.extracted.expiresOn'),
          appValue: appDate,
          docValue: extractedExp,
          isMatch: parseDate(appDate) === parseDate(extractedExp),
        });
      }
    }

    return rows;
  }, [document, applicationData, t]);

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'>
      <div className='bg-white rounded-lg shadow-xl w-full max-w-6xl mx-4 h-[85vh] flex flex-col'>
        <div className='flex items-center justify-between px-6 py-4 border-b border-gray-200'>
          <h2 className='text-lg font-semibold text-gray-900'>{t('documents.preview.title')}</h2>
          <button
            type='button'
            onClick={onClose}
            className='p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100'>
            <X className='h-5 w-5' />
          </button>
        </div>

        <div className='flex-1 overflow-hidden'>
          {isLoading ? (
            <div className='flex items-center justify-center h-full'>
              <Loader2 className='h-8 w-8 animate-spin text-blue-600' />
            </div>
          ) : error ? (
            <div className='flex items-center justify-center h-full'>
              <div className='bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700'>{error}</div>
            </div>
          ) : document && preview ? (
            <div className='flex h-full'>
              <div className='w-3/5 border-r border-gray-200 bg-gray-50 flex flex-col'>
                <div className='flex items-center justify-end px-4 py-2 border-b border-gray-200 bg-white'>
                  <button
                    type='button'
                    onClick={handleOpenNewTab}
                    className='inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-gray-50 text-gray-700 hover:bg-gray-100'>
                    <ExternalLink className='h-3.5 w-3.5' />
                    {t('documents.preview.openNewTab')}
                  </button>
                </div>
                <div className='flex-1 overflow-auto flex items-center justify-center p-4'>
                  {isImageFile(document.filename) ? (
                    <img
                      src={preview.previewUrl}
                      alt={document.filename}
                      className='max-w-full max-h-full object-contain rounded'
                    />
                  ) : (
                    <iframe
                      src={preview.previewUrl}
                      title={document.filename}
                      className='w-full h-full rounded border border-gray-200'
                    />
                  )}
                </div>
              </div>

              <div className='w-2/5 overflow-y-auto p-6 space-y-6'>
                <div className='space-y-3'>
                  <h3 className='text-sm font-semibold text-gray-900 uppercase tracking-wider'>
                    {t('documents.preview.details')}
                  </h3>
                  <dl className='space-y-2'>
                    <div>
                      <dt className='text-xs text-gray-500'>{t('documents.columns.type')}</dt>
                      <dd className='text-sm text-gray-900'>{formatTypeName(document.type)}</dd>
                    </div>
                    <div>
                      <dt className='text-xs text-gray-500'>{t('documents.columns.category')}</dt>
                      <dd className='text-sm text-gray-900'>{formatTypeName(document.category)}</dd>
                    </div>
                    <div>
                      <dt className='text-xs text-gray-500'>{t('documents.preview.filename')}</dt>
                      <dd className='text-sm text-gray-900 break-all'>{document.filename}</dd>
                    </div>
                    <div>
                      <dt className='text-xs text-gray-500'>{t('documents.columns.status')}</dt>
                      <dd className='mt-0.5'>
                        <DocumentStatusBadge status={document.status} />
                      </dd>
                    </div>
                    <div>
                      <dt className='text-xs text-gray-500'>{t('documents.columns.uploadedAt')}</dt>
                      <dd className='text-sm text-gray-900'>{formatDate(document.uploadedAt)}</dd>
                    </div>
                  </dl>
                </div>

                {document.flags ? (
                  <div className='space-y-2'>
                    <h3 className='text-sm font-semibold text-gray-900 uppercase tracking-wider'>
                      {t('documents.columns.flags')}
                    </h3>
                    <DocumentFlagIcons flags={document.flags} />
                    {flagDetails.length > 0 ? (
                      <ul className='space-y-1'>
                        {flagDetails.map((detail) => (
                          <li
                            key={detail}
                            className='text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1'>
                            {detail}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ) : null}

                {crossValidationRows.length > 0 ? (
                  <div className='space-y-2'>
                    <h3 className='text-sm font-semibold text-gray-900 uppercase tracking-wider'>
                      {t('warnings.crossValidation.title')}
                    </h3>
                    <table className='w-full text-xs'>
                      <thead>
                        <tr className='border-b border-gray-200'>
                          <th className='text-left py-1 text-gray-500 font-medium'>
                            {t('warnings.crossValidation.columnField')}
                          </th>
                          <th className='text-left py-1 text-gray-500 font-medium'>
                            {t('warnings.crossValidation.columnApplication')}
                          </th>
                          <th className='text-left py-1 text-gray-500 font-medium'>
                            {t('warnings.crossValidation.columnDocument')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {crossValidationRows.map((row, index) => (
                          <tr key={`${row.field}-${index}`} className='border-b border-gray-100'>
                            <td className='py-1.5 text-gray-700'>{row.field}</td>
                            <td className='py-1.5 text-gray-900'>{row.appValue}</td>
                            <td className={cn('py-1.5', row.isMatch ? 'text-green-700' : 'text-red-700 font-medium')}>
                              {row.docValue} {row.isMatch ? '\u2713' : '\u2717'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}

                {extractedHasValues ? (
                  <div className='space-y-3'>
                    <h3 className='text-sm font-semibold text-gray-900 uppercase tracking-wider'>
                      {t('documents.preview.extractedFields')}
                    </h3>
                    <dl className='space-y-2'>
                      {extractedView?.name ? (
                        <div>
                          <dt className='text-xs text-gray-500'>{t('documents.preview.extracted.name')}</dt>
                          <dd className='text-sm text-gray-900'>{extractedView.name}</dd>
                        </div>
                      ) : null}
                      {extractedView?.dob ? (
                        <div>
                          <dt className='text-xs text-gray-500'>{t('documents.preview.extracted.dob')}</dt>
                          <dd className='text-sm text-gray-900'>{extractedView.dob}</dd>
                        </div>
                      ) : null}
                      {extractedView?.number ? (
                        <div>
                          <dt className='text-xs text-gray-500'>{t('documents.preview.extracted.number')}</dt>
                          <dd className='text-sm text-gray-900'>{extractedView.number}</dd>
                        </div>
                      ) : null}
                      {extractedView?.expiresOn ? (
                        <div>
                          <dt className='text-xs text-gray-500'>{t('documents.preview.extracted.expiresOn')}</dt>
                          <dd className='text-sm text-gray-900'>{extractedView.expiresOn}</dd>
                        </div>
                      ) : null}
                      {extractedView?.issuedDate ? (
                        <div>
                          <dt className='text-xs text-gray-500'>{t('documents.preview.extracted.issuedDate')}</dt>
                          <dd className='text-sm text-gray-900'>{extractedView.issuedDate}</dd>
                        </div>
                      ) : null}
                      {extractedView?.issuer ? (
                        <div>
                          <dt className='text-xs text-gray-500'>{t('documents.preview.extracted.issuer')}</dt>
                          <dd className='text-sm text-gray-900'>{extractedView.issuer}</dd>
                        </div>
                      ) : null}
                      {extractedView?.address ? (
                        <div>
                          <dt className='text-xs text-gray-500'>{t('documents.preview.extracted.address')}</dt>
                          <dd className='text-sm text-gray-900'>{extractedView.address}</dd>
                        </div>
                      ) : null}
                      {extractedView?.sex ? (
                        <div>
                          <dt className='text-xs text-gray-500'>{t('documents.preview.extracted.sex')}</dt>
                          <dd className='text-sm text-gray-900'>{extractedView.sex}</dd>
                        </div>
                      ) : null}
                      {extractedView?.height ? (
                        <div>
                          <dt className='text-xs text-gray-500'>{t('documents.preview.extracted.height')}</dt>
                          <dd className='text-sm text-gray-900'>{extractedView.height}</dd>
                        </div>
                      ) : null}
                      {extractedView?.eyeColor ? (
                        <div>
                          <dt className='text-xs text-gray-500'>{t('documents.preview.extracted.eyeColor')}</dt>
                          <dd className='text-sm text-gray-900'>{extractedView.eyeColor}</dd>
                        </div>
                      ) : null}
                      {extractedView?.licenseClass ? (
                        <div>
                          <dt className='text-xs text-gray-500'>{t('documents.preview.extracted.licenseClass')}</dt>
                          <dd className='text-sm text-gray-900'>{extractedView.licenseClass}</dd>
                        </div>
                      ) : null}
                      {extractedView?.restrictions ? (
                        <div>
                          <dt className='text-xs text-gray-500'>{t('documents.preview.extracted.restrictions')}</dt>
                          <dd className='text-sm text-gray-900'>{extractedView.restrictions}</dd>
                        </div>
                      ) : null}
                      {extractedView?.endorsements ? (
                        <div>
                          <dt className='text-xs text-gray-500'>{t('documents.preview.extracted.endorsements')}</dt>
                          <dd className='text-sm text-gray-900'>{extractedView.endorsements}</dd>
                        </div>
                      ) : null}
                      {extractedView?.confidence !== undefined ? (
                        <div>
                          <dt className='text-xs text-gray-500'>{t('documents.preview.extracted.confidence')}</dt>
                          <dd className='text-sm text-gray-900'>{Math.round(extractedView.confidence)}%</dd>
                        </div>
                      ) : null}
                    </dl>
                  </div>
                ) : document.status === 'uploaded' || document.status === 'flagged' ? (
                  <div className='text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2'>
                    {t('documents.preview.ocrPending')}
                  </div>
                ) : null}

                {document.rejectionReason ? (
                  <div className='space-y-2'>
                    <h3 className='text-sm font-semibold text-gray-900 uppercase tracking-wider'>
                      {t('documents.preview.rejectionReason')}
                    </h3>
                    <p className='text-sm text-red-700 bg-red-50 rounded-md px-3 py-2'>{document.rejectionReason}</p>
                  </div>
                ) : null}

                <div className='space-y-2 pt-2 border-t border-gray-200'>
                  <h3 className='text-sm font-semibold text-gray-900 uppercase tracking-wider'>
                    {t('documents.preview.actions')}
                  </h3>
                  <VerificationControls
                    documentId={document.documentId}
                    status={document.status as DocumentStatus}
                    onVerify={handleVerify}
                    onReject={handleReject}
                    onReupload={handleReupload}
                  />
                  {document.status === 'verified' ? (
                    <p className='text-xs text-green-600'>{t('documents.preview.alreadyVerified')}</p>
                  ) : null}
                  {document.status === 'rejected' ? (
                    <p className='text-xs text-red-600'>{t('documents.preview.alreadyRejected')}</p>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
